/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        CAT Translation Studio State Management Store (Task 10.5.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export type SegmentStatus = 'untranslated' | 'draft' | 'reviewed' | 'approved';
export type SegmentFilter = 'all' | 'untranslated' | 'draft' | 'reviewed' | 'approved' | 'has_warnings';

export interface CatSegment {
  id: string;
  chunkIndex: number; // 1-based display index
  sequenceOrder: number; // 0-based
  sourceText: string;
  targetText: string;
  status: SegmentStatus;
  matchType: 'exact' | 'fuzzy' | 'mt' | 'manual' | null;
  tmMatchPercent: number;
  tmSuggestion?: {
    text: string;
    matchPercent: number;
    sourcePhrase?: string;
  };
  mtSuggestion?: {
    text: string;
    provider: string;
  };
  qaWarnings: string[];
  wordCount: number;
  charCount: number;
  lastSavedAt?: string;
}

export interface CatProject {
  id: string;
  title: string;
  sourceLang: string;
  targetLang: string;
  domain: string;
  createdAt: string;
}

export interface ProjectStats {
  total: number;
  untranslated: number;
  draft: number;
  approved: number;
  hasWarnings: number;
  progressPercent: number;
}

export type CatStatusFilter = SegmentFilter;

export interface GlossaryTerm {
  id: string;
  sourceTerm: string;
  targetTerm: string;
  domain: string;
  forbiddenTerms?: string[];
  notes?: string;
}

export interface CatEditorState {
  project: CatProject;
  metadata: {
    documentName: string;
    sourceLang: string;
    targetLang: string;
    domain: string;
  };
  segments: CatSegment[];
  glossary: GlossaryTerm[];
  activeSegmentId: string | null;
  statusFilter: SegmentFilter;
  searchQuery: string;
  autoJumpOnCommit: boolean;
  tmLearnedCount: number;

  // Actions
  setActiveSegmentId: (id: string | null) => void;
  setStatusFilter: (filter: SegmentFilter) => void;
  setSearchQuery: (query: string) => void;
  setAutoJumpOnCommit: (enabled: boolean) => void;
  toggleAutoJump: () => void;

  updateTargetText: (id: string, text: string) => void;
  insertTermIntoActiveSegment: (termText: string) => void;
  commitSegment: (id: string, nextStatus?: 'draft' | 'approved') => void;
  approveSegment: (id: string) => void;
  applySuggestion: (id: string, type: 'tm' | 'mt') => void;
  navigateSegment: (direction: 'next' | 'prev') => void;

  // Selectors
  getFilteredSegments: () => CatSegment[];
  filteredSegments: () => CatSegment[];
  getProjectStats: () => ProjectStats;
  calculateProgress: () => {
    totalSegments: number;
    approvedSegments: number;
    draftSegments: number;
    untranslatedSegments: number;
    percent: number;
  };
  getMatchedGlossaryTerms: (sourceText: string) => GlossaryTerm[];
  resetProject: () => void;
}

/**
 * Checks for QA linter inconsistencies between source and target text.
 */
export function checkQaWarnings(
  source: string,
  target: string,
  glossary?: GlossaryTerm[]
): string[] {
  const warnings: string[] = [];
  if (!target.trim()) return warnings;

  // 1. Variable Placeholders Check {var} or {{var}}
  const sourceVars: string[] = source.match(/\{+[a-zA-Z0-9_]+\}+/g) || [];
  const targetVars: string[] = target.match(/\{+[a-zA-Z0-9_]+\}+/g) || [];
  for (const sv of sourceVars) {
    if (!targetVars.includes(sv)) {
      warnings.push(`Missing placeholder ${sv}`);
    }
  }

  // 2. HTML Tag Check <tag>
  const sourceTags: string[] = source.match(/<\/?[a-zA-Z0-9]+[^>]*>/g) || [];
  const targetTags: string[] = target.match(/<\/?[a-zA-Z0-9]+[^>]*>/g) || [];
  for (const st of sourceTags) {
    if (!targetTags.includes(st)) {
      warnings.push(`Missing tag ${st}`);
    }
  }

  // 3. Numbers Mismatch Check (e.g. source has 2026, target has 2025 or missing)
  const sourceNumbers: string[] = source.match(/\b\d+(?:[\.,]\d+)?\b/g) || [];
  const targetNumbers: string[] = target.match(/\b\d+(?:[\.,]\d+)?\b/g) || [];
  for (const num of sourceNumbers) {
    if (!targetNumbers.includes(num)) {
      warnings.push(`Number mismatch: '${num}' in source not found in target`);
    }
  }

  // 4. Trailing Punctuation Check
  const lastSourceChar = source.trim().slice(-1);
  const lastTargetChar = target.trim().slice(-1);
  if (['.', '?', '!'].includes(lastSourceChar) && lastTargetChar !== lastSourceChar) {
    warnings.push(`Punctuation mismatch: expected '${lastSourceChar}' at end`);
  }

  // 5. Abnormal Length Expansion (> 220%)
  if (source.length > 20 && target.length > source.length * 2.2) {
    warnings.push('Length expansion exceeds 220%');
  }

  // 6. Forbidden Glossary Terms Check
  if (glossary && glossary.length > 0) {
    for (const term of glossary) {
      if (term.forbiddenTerms) {
        for (const forbidden of term.forbiddenTerms) {
          if (target.toLowerCase().includes(forbidden.toLowerCase())) {
            warnings.push(
              `Forbidden term used: '${forbidden}' (Recommended: '${term.targetTerm}')`
            );
          }
        }
      }
    }
  }

  return warnings;
}

export const SEED_CAT_SEGMENTS: CatSegment[] = [
  {
    id: 'seg-001',
    chunkIndex: 1,
    sequenceOrder: 0,
    sourceText: 'Welcome to the <brand>Sophia Platform</brand> Enterprise Edge Portal.',
    targetText: 'Chào mừng bạn đến với Cổng thông tin Doanh nghiệp <brand>Sophia Platform</brand>.',
    status: 'approved',
    matchType: 'exact',
    tmMatchPercent: 100,
    tmSuggestion: {
      text: 'Chào mừng bạn đến với Cổng thông tin Doanh nghiệp <brand>Sophia Platform</brand>.',
      matchPercent: 100,
      sourcePhrase: 'Welcome to the <brand>Sophia Platform</brand> Enterprise Edge Portal.',
    },
    mtSuggestion: {
      text: 'Chào mừng đến với cổng Sophia Platform.',
      provider: 'DeepSeek-V3',
    },
    qaWarnings: [],
    wordCount: 8,
    charCount: 69,
  },
  {
    id: 'seg-002',
    chunkIndex: 2,
    sequenceOrder: 1,
    sourceText: 'Your current balance is <val>{credit_balance}</val> AI tokens remaining.',
    targetText: 'Số dư hiện tại của bạn còn <val>{credit_balance}</val> mã AI.',
    status: 'draft',
    matchType: 'fuzzy',
    tmMatchPercent: 92,
    tmSuggestion: {
      text: 'Số dư hiện tại của bạn còn <val>{credit_balance}</val> điểm thưởng.',
      matchPercent: 92,
      sourcePhrase: 'Your current balance is <val>{credit_balance}</val> reward points.',
    },
    mtSuggestion: {
      text: 'Số dư hiện tại của bạn là <val>{credit_balance}</val> token AI còn lại.',
      provider: 'GPT-4o',
    },
    qaWarnings: [],
    wordCount: 9,
    charCount: 73,
  },
  {
    id: 'seg-003',
    chunkIndex: 3,
    sequenceOrder: 2,
    sourceText: 'Do not share your private key <secret>{api_key}</secret> with unauthorized team members.',
    targetText: '',
    status: 'untranslated',
    matchType: null,
    tmMatchPercent: 0,
    tmSuggestion: {
      text: 'Không chia sẻ khóa bí mật <secret>{api_key}</secret> với thành viên nhóm không có quyền.',
      matchPercent: 88,
      sourcePhrase: 'Never share your secret key <secret>{api_key}</secret> with unauthorized members.',
    },
    mtSuggestion: {
      text: 'Không chia sẻ khóa riêng tư <secret>{api_key}</secret> của bạn với các thành viên nhóm trái phép.',
      provider: 'Claude-3.5',
    },
    qaWarnings: [],
    wordCount: 12,
    charCount: 88,
  },
  {
    id: 'seg-004',
    chunkIndex: 4,
    sequenceOrder: 3,
    sourceText: 'Cloudflare Workers execute requests at sub-millisecond cold starts across 330+ cities worldwide.',
    targetText: 'Cloudflare Workers thực thi các yêu cầu với thời gian khởi động lạnh dưới 1 mili-giây tại hơn 330 thành phố trên toàn thế giới.',
    status: 'approved',
    matchType: 'exact',
    tmMatchPercent: 100,
    tmSuggestion: {
      text: 'Cloudflare Workers thực thi các yêu cầu với thời gian khởi động lạnh dưới 1 mili-giây tại hơn 330 thành phố trên toàn thế giới.',
      matchPercent: 100,
    },
    qaWarnings: [],
    wordCount: 13,
    charCount: 97,
  },
  {
    id: 'seg-005',
    chunkIndex: 5,
    sequenceOrder: 4,
    sourceText: 'Click <link href="/docs">here</link> to inspect our OpenAPI 3.1 contract specifications.',
    targetText: 'Bấm <link href="/docs">vào đây</link> để xem đặc tả hợp đồng OpenAPI 3.1.',
    status: 'draft',
    matchType: 'fuzzy',
    tmMatchPercent: 85,
    tmSuggestion: {
      text: 'Nhấn <link href="/docs">tại đây</link> để kiểm tra tài liệu OpenAPI.',
      matchPercent: 85,
    },
    mtSuggestion: {
      text: 'Bấm vào đây để kiểm tra thông số kỹ thuật hợp đồng OpenAPI 3.1 của chúng tôi.',
      provider: 'Gemini-1.5',
    },
    qaWarnings: [],
    wordCount: 10,
    charCount: 88,
  },
  {
    id: 'seg-006',
    chunkIndex: 6,
    sequenceOrder: 5,
    sourceText: 'All translation segments are auto-indexed into D1 vector memory for real-time fuzzy matching.',
    targetText: 'Tất cả các phân đoạn dịch đều được tự động lập chỉ mục vào bộ nhớ vector D1 để so khớp mờ theo thời gian thực.',
    status: 'draft',
    matchType: 'mt',
    tmMatchPercent: 78,
    mtSuggestion: {
      text: 'Tất cả các phân đoạn dịch đều được tự động lập chỉ mục vào bộ nhớ vector D1 để so khớp mờ thời gian thực.',
      provider: 'DeepSeek-V3',
    },
    qaWarnings: [],
    wordCount: 14,
    charCount: 94,
  },
  {
    id: 'seg-007',
    chunkIndex: 7,
    sequenceOrder: 6,
    sourceText: 'Please verify that your database schema version matches <code className="ver">v2.1.0</code>.',
    targetText: 'Vui lòng xác minh phiên bản lược đồ cơ sở dữ liệu khớp với v2.1.0.',
    status: 'draft',
    matchType: 'manual',
    tmMatchPercent: 65,
    mtSuggestion: {
      text: 'Vui lòng xác minh rằng phiên bản lược đồ cơ sở dữ liệu của bạn khớp với <code className="ver">v2.1.0</code>.',
      provider: 'GPT-4o',
    },
    qaWarnings: ['Missing tag <code className="ver">', 'Missing tag </code>'], // Intentionally seeded QA warning
    wordCount: 11,
    charCount: 93,
  },
  {
    id: 'seg-008',
    chunkIndex: 8,
    sequenceOrder: 7,
    sourceText: 'Translations are cached at the edge for 24 hours to maximize performance.',
    targetText: '',
    status: 'untranslated',
    matchType: null,
    tmMatchPercent: 0,
    tmSuggestion: {
      text: 'Bản dịch được lưu trữ đệm tại rìa mạng trong 24 giờ để tối ưu hóa hiệu năng.',
      matchPercent: 95,
      sourcePhrase: 'Translations are cached at edge locations for 24 hours to maximize performance.',
    },
    mtSuggestion: {
      text: 'Bản dịch được lưu vào bộ nhớ cache ở rìa mạng trong 24 giờ nhằm tối đa hóa hiệu suất.',
      provider: 'Claude-3.5',
    },
    qaWarnings: [],
    wordCount: 11,
    charCount: 75,
  },
  {
    id: 'seg-009',
    chunkIndex: 9,
    sequenceOrder: 8,
    sourceText: 'Use the shortcut <kbd>Ctrl+Enter</kbd> to save and jump to the next segment.',
    targetText: 'Sử dụng phím tắt <kbd>Ctrl+Enter</kbd> để lưu và chuyển sang phân đoạn kế tiếp.',
    status: 'approved',
    matchType: 'exact',
    tmMatchPercent: 100,
    tmSuggestion: {
      text: 'Sử dụng phím tắt <kbd>Ctrl+Enter</kbd> để lưu và chuyển sang phân đoạn kế tiếp.',
      matchPercent: 100,
    },
    qaWarnings: [],
    wordCount: 12,
    charCount: 75,
  },
  {
    id: 'seg-010',
    chunkIndex: 10,
    sequenceOrder: 9,
    sourceText: 'Export your translated document into DOCX, PDF, XLIFF 2.0 or bilingual JSON.',
    targetText: '',
    status: 'untranslated',
    matchType: null,
    tmMatchPercent: 0,
    mtSuggestion: {
      text: 'Xuất tài liệu đã dịch của bạn sang DOCX, PDF, XLIFF 2.0 hoặc JSON song ngữ.',
      provider: 'DeepSeek-V3',
    },
    qaWarnings: [],
    wordCount: 12,
    charCount: 77,
  },
  {
    id: 'seg-011',
    chunkIndex: 11,
    sequenceOrder: 10,
    sourceText: 'Are you sure you want to revert all changes to this segment?',
    targetText: 'Bạn có chắc chắn muốn hoàn tác tất cả các thay đổi đối với phân đoạn này không?',
    status: 'approved',
    matchType: 'exact',
    tmMatchPercent: 100,
    qaWarnings: [],
    wordCount: 12,
    charCount: 60,
  },
  {
    id: 'seg-012',
    chunkIndex: 12,
    sequenceOrder: 11,
    sourceText: 'Press <kbd>Ctrl+1</kbd> to accept the top Translation Memory match.',
    targetText: '',
    status: 'untranslated',
    matchType: null,
    tmMatchPercent: 0,
    tmSuggestion: {
      text: 'Nhấn <kbd>Ctrl+1</kbd> để chấp nhận gợi ý bộ nhớ dịch hàng đầu.',
      matchPercent: 90,
    },
    mtSuggestion: {
      text: 'Nhấn <kbd>Ctrl+1</kbd> để áp dụng kết quả khớp Bộ nhớ dịch cao nhất.',
      provider: 'GPT-4o',
    },
    qaWarnings: [],
    wordCount: 10,
    charCount: 67,
  },
];

export const SEED_GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    id: 'term-001',
    sourceTerm: 'Edge Computing',
    targetTerm: 'Điện toán Biên',
    domain: 'Infrastructure',
    forbiddenTerms: ['Điện toán rìa', 'Máy tính cạnh'],
    notes: 'Capitalize "Biên" in official Cloudflare documentation',
  },
  {
    id: 'term-002',
    sourceTerm: 'Zero-Egress',
    targetTerm: 'Không phí truyền tải',
    domain: 'Cloudflare R2',
    forbiddenTerms: ['Không lối ra', 'Zero egress'],
    notes: 'Emphasize $0 bandwidth egress fee benefit',
  },
  {
    id: 'term-003',
    sourceTerm: 'Cold Start',
    targetTerm: 'Khởi động nguội',
    domain: 'Serverless',
    forbiddenTerms: ['Bắt đầu lạnh'],
    notes: 'V8 isolate sub-millisecond startup latency',
  },
  {
    id: 'term-004',
    sourceTerm: 'Translation Memory',
    targetTerm: 'Bộ nhớ dịch thuật',
    domain: 'CAT Studio',
    forbiddenTerms: ['Ký ức dịch'],
    notes: 'Standard industry CAT term (TM)',
  },
  {
    id: 'term-005',
    sourceTerm: 'AI Gateway',
    targetTerm: 'Cổng kết nối AI',
    domain: 'Cloudflare AI',
    notes: 'Unified routing and caching layer',
  },
  {
    id: 'term-006',
    sourceTerm: 'Sophia Platform',
    targetTerm: 'Nền tảng Sophia',
    domain: 'Brand',
    notes: 'Enterprise platform brand name',
  },
];

export const useCatEditorStore = create<CatEditorState>((set, get) => ({
  project: {
    id: 'proj-cat-001',
    title: 'Cloudflare Edge Developer Documentation & Portal',
    sourceLang: 'en-US',
    targetLang: 'vi-VN',
    domain: 'tech',
    createdAt: '2026-10-25T07:00:00.000Z',
  },
  metadata: {
    documentName: 'Cloudflare Edge Developer Documentation & Portal',
    sourceLang: 'en-US',
    targetLang: 'vi-VN',
    domain: 'tech',
  },
  segments: SEED_CAT_SEGMENTS,
  glossary: SEED_GLOSSARY_TERMS,
  activeSegmentId: 'seg-001',
  statusFilter: 'all',
  searchQuery: '',
  autoJumpOnCommit: true,
  tmLearnedCount: 4,

  setActiveSegmentId: (id) => set({ activeSegmentId: id }),
  setStatusFilter: (filter) => set({ statusFilter: filter }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setAutoJumpOnCommit: (enabled) => set({ autoJumpOnCommit: enabled }),
  toggleAutoJump: () => set((state) => ({ autoJumpOnCommit: !state.autoJumpOnCommit })),

  updateTargetText: (id, text) => {
    const { glossary } = get();
    set((state) => ({
      segments: state.segments.map((seg) => {
        if (seg.id !== id) return seg;
        const warnings = checkQaWarnings(seg.sourceText, text, glossary);
        // In CAT workflow: editing target text moves segment to 'draft'. If cleared, moves to 'untranslated'.
        const status = text.trim().length > 0 ? 'draft' : 'untranslated';
        return {
          ...seg,
          targetText: text,
          status,
          qaWarnings: warnings,
          lastSavedAt: new Date().toISOString(),
        };
      }),
    }));
  },

  insertTermIntoActiveSegment: (termText) => {
    const { activeSegmentId, segments } = get();
    if (!activeSegmentId) return;

    const seg = segments.find((s) => s.id === activeSegmentId);
    if (!seg) return;

    const newTarget = seg.targetText ? `${seg.targetText} ${termText}` : termText;
    get().updateTargetText(activeSegmentId, newTarget);
  },

  commitSegment: (id, nextStatus = 'draft') => {
    const { segments, autoJumpOnCommit } = get();
    const currentSegment = segments.find((s) => s.id === id);
    if (!currentSegment) return;

    const warnings = checkQaWarnings(currentSegment.sourceText, currentSegment.targetText);

    // Save segment and increment learned TM counter
    set((state) => ({
      tmLearnedCount: state.tmLearnedCount + 1,
      segments: state.segments.map((seg) =>
        seg.id === id
          ? {
              ...seg,
              status: nextStatus,
              qaWarnings: warnings,
              lastSavedAt: new Date().toISOString(),
            }
          : seg
      ),
    }));

    // Auto-jump to next untranslated or draft segment if enabled
    if (autoJumpOnCommit) {
      const filtered = get().getFilteredSegments();
      const currentIndex = filtered.findIndex((s) => s.id === id);

      if (currentIndex !== -1) {
        // Find next untranslated/draft segment after current index
        const nextTarget =
          filtered.slice(currentIndex + 1).find((s) => s.status === 'untranslated' || s.status === 'draft') ||
          filtered.find((s) => s.status === 'untranslated');

        if (nextTarget) {
          set({ activeSegmentId: nextTarget.id });
        } else if (currentIndex + 1 < filtered.length) {
          set({ activeSegmentId: filtered[currentIndex + 1].id });
        }
      }
    }
  },

  approveSegment: (id) => {
    set((state) => ({
      segments: state.segments.map((seg) =>
        seg.id === id
          ? {
              ...seg,
              status: seg.status === 'approved' ? 'draft' : 'approved',
              lastSavedAt: new Date().toISOString(),
            }
          : seg
      ),
    }));
  },

  applySuggestion: (id, type) => {
    const { segments } = get();
    const seg = segments.find((s) => s.id === id);
    if (!seg) return;

    let textToApply = '';
    let matchType: 'exact' | 'fuzzy' | 'mt' = 'mt';

    if (type === 'tm' && seg.tmSuggestion) {
      textToApply = seg.tmSuggestion.text;
      matchType = seg.tmSuggestion.matchPercent === 100 ? 'exact' : 'fuzzy';
    } else if (type === 'mt' && seg.mtSuggestion) {
      textToApply = seg.mtSuggestion.text;
      matchType = 'mt';
    }

    if (textToApply) {
      const warnings = checkQaWarnings(seg.sourceText, textToApply);
      set((state) => ({
        segments: state.segments.map((s) =>
          s.id === id
            ? {
                ...s,
                targetText: textToApply,
                status: 'draft',
                matchType,
                qaWarnings: warnings,
                lastSavedAt: new Date().toISOString(),
              }
            : s
        ),
      }));
    }
  },

  navigateSegment: (direction) => {
    const { activeSegmentId } = get();
    const filtered = get().getFilteredSegments();
    if (filtered.length === 0) return;

    const currentIndex = filtered.findIndex((s) => s.id === activeSegmentId);
    if (currentIndex === -1) {
      set({ activeSegmentId: filtered[0].id });
      return;
    }

    if (direction === 'next' && currentIndex < filtered.length - 1) {
      set({ activeSegmentId: filtered[currentIndex + 1].id });
    } else if (direction === 'prev' && currentIndex > 0) {
      set({ activeSegmentId: filtered[currentIndex - 1].id });
    }
  },

  getFilteredSegments: () => {
    const { segments, statusFilter, searchQuery } = get();

    return segments.filter((seg) => {
      // 1. Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchSource = seg.sourceText.toLowerCase().includes(q);
        const matchTarget = seg.targetText.toLowerCase().includes(q);
        if (!matchSource && !matchTarget) return false;
      }

      // 2. Status filter
      if (statusFilter === 'all') return true;
      if (statusFilter === 'has_warnings') return seg.qaWarnings.length > 0;
      return seg.status === statusFilter;
    });
  },

  filteredSegments: () => get().getFilteredSegments(),

  getProjectStats: () => {
    const { segments } = get();
    const total = segments.length;
    if (total === 0) {
      return { total: 0, untranslated: 0, draft: 0, approved: 0, hasWarnings: 0, progressPercent: 0 };
    }

    const untranslated = segments.filter((s) => s.status === 'untranslated').length;
    const draft = segments.filter((s) => s.status === 'draft').length;
    const approved = segments.filter((s) => s.status === 'approved').length;
    const hasWarnings = segments.filter((s) => s.qaWarnings.length > 0).length;

    // Progress = (draft + approved) / total * 100
    const translatedCount = draft + approved;
    const progressPercent = Math.round((translatedCount / total) * 100);

    return {
      total,
      untranslated,
      draft,
      approved,
      hasWarnings,
      progressPercent,
    };
  },

  calculateProgress: () => {
    const stats = get().getProjectStats();
    return {
      totalSegments: stats.total,
      approvedSegments: stats.approved,
      draftSegments: stats.draft,
      untranslatedSegments: stats.untranslated,
      percent: stats.progressPercent,
    };
  },

  getMatchedGlossaryTerms: (sourceText: string) => {
    const { glossary } = get();
    if (!sourceText.trim() || glossary.length === 0) return [];

    const lowerSource = sourceText.toLowerCase();
    return glossary.filter((term) =>
      lowerSource.includes(term.sourceTerm.toLowerCase())
    );
  },

  resetProject: () =>
    set({
      segments: [...SEED_CAT_SEGMENTS],
      glossary: [...SEED_GLOSSARY_TERMS],
      activeSegmentId: 'seg-001',
      statusFilter: 'all',
      searchQuery: '',
      tmLearnedCount: 4,
    }),
}));
